import { html } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { MediaPlayerBaseComponent } from '../media-player.component';
import {
  BluebillywigPlayerStateEnum,
  BlueBillywigPlayerStateIconsEnum,
  BluebillywigAssetMediaType,
  BluebillywigAssetData,
  NativeAudioAsset,
} from '../media-player.type';
import { styles, tag } from './audio-inline.styles';

@customElement(tag)
export class MediaPlayerAudioInlineComponent extends MediaPlayerBaseComponent {
  static override styles = styles;

  @query('audio')
  audio!: HTMLAudioElement;

  @state()
  audioAssets: Array<NativeAudioAsset> = [];

  override connectedCallback() {
    super.connectedCallback();

    this.#initAudioSource();
  }

  async #initAudioSource() {
    this.currentPlayerState = BluebillywigPlayerStateEnum.Loading;

    const json = await this.getJson();

    this.audioAssets = this.#getAudioAssets(
      json.clipData.assets,
      json.publicationData.defaultMediaAssetPath,
    );
  }

  #getAudioAssets(assets: Array<BluebillywigAssetData>, uri: string) {
    return assets
      .sort((a, b) => {
        if (a.mediatype === 'MP3' && b.mediatype !== 'MP3') {
          return 1;
        }
        return Number(a.bandwidth) > Number(b.bandwidth) ? -1 : 1;
      })
      .map((asset) => {
        return {
          source: uri + asset.src,
          type: BluebillywigAssetMediaType[asset.mediatype] ?? null,
        };
      });
  }

  protected override togglePlayState() {
    if (!this.audio) {
      return;
    }

    if (this.currentPlayerState === BluebillywigPlayerStateEnum.Paused) {
      this.#play();
    } else {
      this.#pause();
    }
  }

  #play() {
    if (!this.audio) {
      return;
    }

    this.audio.play();
    this.currentPlayerState = BluebillywigPlayerStateEnum.Playing;
  }

  #pause() {
    if (!this.audio) {
      return;
    }

    this.audio.pause();
    this.audio.currentTime = 0;
    this.currentPlayerState = BluebillywigPlayerStateEnum.Paused;
  }

  override render() {
    return html`${this.#audioObjectTemplate()}${this.buttonTemplate()}`;
  }

  buttonTemplate() {
    const classes = {
      playing: this.playing,
      paused: this.paused,
      idle: this.idle,
      button: true,
    };

    return html`<button
      aria-label="${`audio player inline with status ${this.currentPlayerState}` ||
      'loader'}"
      @click="${this.togglePlayState}"
      class=${classMap(classes)}
      type="button"
      data-test="${tag}-button"
    >
      ${unsafeHTML(
        `<a-icon class="icon" name=${
          BlueBillywigPlayerStateIconsEnum[this.currentPlayerState || 'loader']
        }></a-icon>`,
      )}
    </button>`;
  }

  #audioObjectTemplate() {
    if (this.audioAssets.length === 0) {
      return html``;
    }

    return html`
      <audio
        ref="audio"
        @canplaythrough="${this.audioCanPlayThrough}"
        @ended="${this.audioEnded}"
      >
        ${this.#sourceTemplates()}
      </audio>
    `;
  }

  #sourceTemplates() {
    return this.audioAssets.map((audioAsset) => {
      return html`<source
        src="${audioAsset.source}"
        type="${audioAsset.type}"
      />`;
    });
  }

  audioCanPlayThrough() {
    if (this.currentPlayerState === BluebillywigPlayerStateEnum.Loading) {
      this.currentPlayerState = BluebillywigPlayerStateEnum.Paused;
    }
  }

  audioEnded() {
    this.currentPlayerState = BluebillywigPlayerStateEnum.Paused;
  }

  override onInvisible() {
    if (this.currentPlayerState === BluebillywigPlayerStateEnum.Playing) {
      this.#pause();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-audio-inline-player': MediaPlayerAudioInlineComponent;
  }
}
